import * as React from 'react'

import { classList } from 'react-classlist-helper'

import type {
  TableFieldDef,
  TableFields,
} from '~/client/src/desktop/tables/tableFields'
import { buildTableFields } from '~/client/src/desktop/tables/tableFields'
import { Button } from '~/client/src/shared/components/Button/Button'
import { emptyIfBlank } from '~/client/src/shared/lists/core'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import UserOrBrandCell from '../UserOrBrandCell'
import { IResourcePropertyRow, ResourcePropertiesColumnId } from './types'

interface ResourcePropertyRowActionHandlers {
  onEdit: (id: string) => void
}

interface ResourcePropertyFieldsArgs {
  readonly rowActionHandlers: ResourcePropertyRowActionHandlers
}

export function buildResourcePropertyFields({
  rowActionHandlers,
}: ResourcePropertyFieldsArgs): TableFields<IResourcePropertyRow> {
  const t = Localization.translator
  const fields: readonly TableFieldDef<IResourcePropertyRow>[] = [
    {
      key: ResourcePropertiesColumnId.Label,
      label: t.label,
      value: row => emptyIfBlank(row.label),
      sortable: true,
      groupable: true,
      column: {
        size: 260,
        cell: record => {
          return (
            <span className="tw:flex tw:w-full tw:items-center tw:gap-2">
              <span
                className={classList({
                  'tw:flex-1 tw:truncate tw:text-body-md tw:leading-6': true,
                  'tw:text-muted-foreground': record.isArchived,
                  'tw:text-foreground': !record.isArchived,
                })}
                title={record.label}
              >
                {record.label}
              </span>
              <Button
                variant="outline"
                className="tw:grow-0!"
                onClick={e => {
                  e.stopPropagation()
                  rowActionHandlers.onEdit(record.id)
                }}
              >
                {t.edit_verb}
              </Button>
            </span>
          )
        },
      },
      fixed: true,
    },
    {
      key: ResourcePropertiesColumnId.Name,
      label: t.internalName,
      value: row => emptyIfBlank(row.name),
      sortable: true,
      groupable: true,
      column: {
        size: 220,
      },
    },
    {
      key: ResourcePropertiesColumnId.Type,
      label: t.type,
      value: row => emptyIfBlank(row.type),
      facetable: true,
      sortable: true,
      groupable: true,
      column: {
        size: 160,
      },
    },
    {
      key: ResourcePropertiesColumnId.Source,
      label: t.createdBy,
      value: row => emptyIfBlank(row.source),
      facetable: true,
      sortable: true,
      groupable: true,
      column: {
        size: 160,
        cell: record => <UserOrBrandCell userId={record.createdBy} />,
      },
    },
    {
      key: ResourcePropertiesColumnId.Status,
      label: t.status,
      value: row => emptyIfBlank(row.status),
      facetable: true,
      sortable: true,
      groupable: true,
      column: {
        size: 120,
      },
    },
    {
      key: ResourcePropertiesColumnId.UpdatedAt,
      label: t.lastUpdated,
      value: row => emptyIfBlank(row.updatedAt),
      sortable: true,
      groupable: true,
      column: {
        size: 250,
      },
    },
  ]

  return buildTableFields({
    getId: row => row.id,
    fields,
  })
}
